// src/pages/RefundPolicy.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const RefundPolicy = () => {
  return (
    <div>
      <Helmet>
        <title>Refund Policy - YouthXConnect</title>
        <meta
          name="description"
          content="Learn about YouthXConnect's Refund Policy, including eligibility, non-refundable situations, and the refund process."
        />
        <meta
          name="keywords"
          content="Refund Policy, YouthXConnect, Refunds, Cancellations, Policies"
        />
        <meta property="og:title" content="Refund Policy - YouthXConnect" />
        <meta
          property="og:description"
          content="Learn about YouthXConnect's Refund Policy, including eligibility, non-refundable situations, and the refund process."
        />
        <meta property="og:image" content="%PUBLIC_URL%/refund-policy-og.png" />
        <meta property="og:url" content="https://www.youthxconnect.com/refund-policy" />
        <meta name="twitter:title" content="Refund Policy - YouthXConnect" />
        <meta
          name="twitter:description"
          content="Learn about YouthXConnect's Refund Policy, including eligibility, non-refundable situations, and the refund process."
        />
        <meta name="twitter:image" content="%PUBLIC_URL%/refund-policy-twitter.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-20 max-w-4xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          className="text-3xl sm:text-4xl font-bold mb-6 text-center text-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          Refund Policy
        </motion.h1>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          Last updated: October 31, 2024
        </motion.p>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          This Refund Policy ("Policy") outlines the terms and conditions under which refunds may or may not be granted by YouthXConnect ("Company", "we", "us", "our"). By purchasing or registering for any of our services, you explicitly acknowledge and agree to be bound by this Policy.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          Limited Refund Eligibility
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          Refunds are strictly limited to circumstances where an event is cancelled solely by YouthXConnect's unilateral decision, without any external factors or force majeure events contributing to such cancellation. The Company reserves the absolute right to determine, at its sole discretion, whether a cancellation qualifies for a refund.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          Comprehensive Non-Refundable Situations
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          Refunds will NOT be provided under any of the following circumstances, including but not limited to:
          <ul className="list-disc pl-6 mt-2">
            <li>Any cancellation initiated by the participant for any reason whatsoever</li>
            <li>No-shows or failure to attend the event, regardless of the reason</li>
            <li>Dissatisfaction with event content, speakers, venue, or any other aspect of the event</li>
            <li>Technical difficulties or connectivity issues experienced during virtual events</li>
            <li>Changes in event format, schedule, speakers, or content</li>
            <li>Force majeure events including but not limited to natural disasters, pandemics, government actions, or civil unrest</li>
            <li>Inability to attend due to personal circumstances, including health issues, travel restrictions, or visa problems</li>
            <li>Partial attendance or early departure from events</li>
            <li>Cancellation due to violation of our terms of service or code of conduct</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
        >
          Processing of Eligible Refunds
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>
          In the rare event that a refund is deemed eligible:
          <ul className="list-disc pl-6 mt-2">
            <li>Refunds will be processed within 14-30 business days from approval</li>
            <li>Processing times may vary based on payment method and financial institutions</li>
            <li>Company bears no responsibility for delays in refund processing by third-party payment processors</li>
            <li>Refunds will be issued only to the original payment method</li>
            <li>Any transaction fees, processing fees, or currency conversion charges are non-refundable</li>
            <li>The Company reserves the right to deduct administrative fees from the refund amount</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.1 }}
        >
          Limitation of Liability
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
          Under no circumstances shall YouthXConnect be liable for any direct, indirect, incidental, special, consequential, or exemplary damages arising from or relating to any refund request or processing, including but not limited to loss of profits, data, use, goodwill, or other intangible losses. The Company's maximum liability shall be limited to the original amount paid for the event registration.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.3 }}
        >
          Dispute Resolution
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
          Any disputes regarding refunds shall be resolved through mandatory arbitration in Delhi, India, conducted in English. By agreeing to this Policy, you waive any right to participate in a class action lawsuit or class-wide arbitration.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
        >
          Contact Us
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
          For any refund-related inquiries, please contact our support team at <a href="mailto:connect@YouthXConnect.com" className="text-blue-500 underline">connect@YouthXConnect.com</a>.
        </motion.p>


        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.8 }}
        >
          Amendments
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }}>
          We reserve the right to amend this Refund Policy at any time. All changes will be posted on this page with an updated revision date.
        </motion.p>
      </motion.div>
      
    </div>
  );
};

export default RefundPolicy;